import * as moment from 'moment';

import { Component, Input, OnInit } from '@angular/core';
import { NonRepeatingEvent } from '../../../../../../../../../models/event-type';
import { AccountService } from '../../../../../../../../../services/account/account.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EventTypeService } from '../../../../../../../../../services/event-type/event-type.service';
import { dateStringFromISO8601, getISO8601DateStringFromMoment } from '../../../../../../../../../utils';
import { LocationService } from '../../../../../../../../../services/location/location.service';

class DuplicateStartDate {
  date: string;
  errorMessage: string;
}

enum RepeatType {
  selectDates,
  interval,
  monthly
}

enum Interval {
  weekly,
  monthly
}

@Component({
  selector: 'app-duplicate-occurrence-modal',
  templateUrl: './duplicate-occurrence-modal.component.html',
  styleUrls: ['./duplicate-occurrence-modal.component.css']
})
export class DuplicateOccurrenceModalComponent implements OnInit {

  @Input() occurrence: NonRepeatingEvent;
  @Input() savedCallback: () => undefined;

  public locationId: number | null = null;
  public repeatType: RepeatType = RepeatType.selectDates;

  // selectDates parameters
  public duplicateStartDates: DuplicateStartDate[] = [
    {
      date: '',
      errorMessage: ''
    }
  ];

  // parameters shared by interval and monthly
  public firstStartDate = '';
  public lastStartDate = '';

  // interval parameters
  public interval: Interval = Interval.weekly;

  // monthly parameters
  public includeSunday = false;
  public includeMonday = false;
  public includeTuesday = false;
  public includeWednesday = false;
  public includeThursday = false;
  public includeFriday = false;
  public includeSaturday = false;

  public serverError: number = null;
  public errorMessage = '';
  public successMessage = '';

  public loading = false;

  public dateStringFromISO8601 = dateStringFromISO8601;
  public RepeatType = RepeatType;
  public Interval = Interval;

  constructor(
    public activeModal: NgbActiveModal,
    public locationService: LocationService,
    private accountService: AccountService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    this.locationId = this.occurrence.location_id;
  }

  public removeDuplicateStartDate(dateIndex: number) {
    this.duplicateStartDates.splice(dateIndex, 1);
  }

  public onSubmit() {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.hasInputErrors()) {
      return;
    }

    if (this.repeatType === RepeatType.selectDates) {
      this.duplicateStartDates = this.duplicateStartDates.filter((dsd) => {
        return dsd.date !== '';
      });
    } else if (this.repeatType === RepeatType.interval) {
      this.setStartDatesPerInterval();
    } else {
      throw new Error('not implemented');
    }

    if (this.duplicateStartDates.length === 0) {
      this.errorMessage = 'Please enter at least one start date.';
      this.duplicateStartDates = [
        {
          date: '',
          errorMessage: ''
        }
      ];
      return;
    }

    if (this.duplicateStartDates.length > 20) {
      this.errorMessage = 'Please create no more than 20 occurrences at once (' + this.duplicateStartDates.length.toString() + ' selected currently).';
    }

    const originalStartDate = moment(
      this.occurrence.event_parts
        .map((ep) => ep.start_date)
        .sort()[0]
    );

    this.loading = true;

    let submitPromise = Promise.resolve();

    const succeededIdxs: number[] = [];

    for (let dupIdx = 0; dupIdx < this.duplicateStartDates.length; dupIdx++) {
      const duplicateStartDate = this.duplicateStartDates[dupIdx];

      duplicateStartDate.errorMessage = '';

      submitPromise = submitPromise.then(() => {
        const thisDateDiff = moment(duplicateStartDate.date).diff(
          originalStartDate,
          'days'
        );

        let thisOccurrenceName = '';
        if (this.occurrence.occurrence_name !== '') {
          thisOccurrenceName = this.occurrence.occurrence_name;
        } else if (this.occurrence.customer_facing_name !== '') {
          thisOccurrenceName = this.occurrence.customer_facing_name;
        }

        if (thisOccurrenceName !== '') {
          thisOccurrenceName = 'Copy of ' + thisOccurrenceName;
          if (this.duplicateStartDates.length > 1) {
            thisOccurrenceName += ' (' + (dupIdx + 1).toString() + ')';
          }
        }

        const thisDuplicateOccurrence: NonRepeatingEvent = {
          id: null,
          event_type_id: this.occurrence.event_type_id,
          event_type_name: this.occurrence.event_type_name,
          location_id: this.locationId,
          location_name: '',
          location_area_id: this.occurrence.location_area_id,
          capacity: this.occurrence.capacity,
          rolling_days: this.occurrence.rolling_days,
          rolling_days_use_exact_date: this.occurrence.rolling_days_use_exact_date,
          booking_open_time: this.occurrence.booking_open_time,
          pricing_matrix_id: this.occurrence.pricing_matrix_id,
          tax_rate_id: this.occurrence.tax_rate_id,
          event_parts: [],
          is_private: this.occurrence.is_private,
          request_form_booking_id: null,
          private_notes: this.occurrence.private_notes,
          occurrence_name: thisOccurrenceName,
          customer_facing_name: this.occurrence.customer_facing_name,
          benefit_membership_id: this.occurrence.benefit_membership_id,
          benefit_membership_name: '',
          benefit_membership_additional_days: this.occurrence.benefit_membership_additional_days,
          created_at: '',
          updated_at: ''
        }
        if (this.occurrence.rolling_days_use_exact_date) {
          thisDuplicateOccurrence.rolling_days += thisDateDiff;
        }
        if (this.locationId !== this.occurrence.location_id) {
          thisDuplicateOccurrence.location_area_id = null;
        }
        for (const ep of this.occurrence.event_parts) {
          thisDuplicateOccurrence.event_parts.push({
            start_time: ep.start_time,
            start_date: getISO8601DateStringFromMoment(
              moment(ep.start_date).add(thisDateDiff, 'days')
            ),
            duration_minutes: ep.duration_minutes,
            instructors: ep.instructors,
            start_datetime_isoformat: '',
            end_datetime_isoformat: ''
          });
        }
        return this.eventTypeService
          .addNonRepeatingEvent(
            this.occurrence.event_type_id,
            thisDuplicateOccurrence,
            null
          )
          .then(() => {
            succeededIdxs.push(dupIdx);
          })
          .catch(reason => {
            if (reason.status === 400) {
              duplicateStartDate.errorMessage = 'Invalid start date.';
            } else if (reason.status === 403) {
              duplicateStartDate.errorMessage = 'You do not have permission to create this occurrence.';
            } else {
              duplicateStartDate.errorMessage = 'Server error: ' + reason.status.toString();
            }
          });
      });
    }

    submitPromise.finally(() => {
      if (succeededIdxs.length === this.duplicateStartDates.length) {
        this.savedCallback();
        this.activeModal.close();
        return;
      }

      // If there were errors, switch to "select dates" so per-date errors can
      // be shown.
      this.repeatType = RepeatType.selectDates;

      if (succeededIdxs.length > 0) {
        this.successMessage = 'Successfully created duplicates with the following start dates: ';
        this.successMessage += succeededIdxs
          .map((idx) => {
            return dateStringFromISO8601(this.duplicateStartDates[idx].date);
          })
          .join('; ');
        this.successMessage += '. The remaining start dates listed above failed.';

        // Must iterate in reverse, otherwise the indexes will shift.
        for (const succeededIdx of succeededIdxs.reverse()) {
          this.duplicateStartDates.splice(succeededIdx, 1);
        }
      } else {
        this.errorMessage = 'All start dates failed.';
      }

      this.loading = false;
    })
  }

  public getStartDateRangeErrorMessage(): string {
    if ((this.firstStartDate === '') || (this.lastStartDate === '')) {
      return '';
    }

    if (this.firstStartDate >= this.lastStartDate) {
      return 'Last start date must be after first start date.'
    }

    return '';
  }

  public hasInputErrors() {
    if (this.repeatType === RepeatType.selectDates) {
      const numInputDates = this.duplicateStartDates
        .filter((dsd) => {
            return dsd.date !== '';
        })
        .length;
      if (numInputDates === 0) {
        return true;
      }
    }

    if ([RepeatType.interval, RepeatType.monthly].includes(this.repeatType)) {
      if ((this.firstStartDate === '') || (this.lastStartDate === '')) {
        return true;
      }
      if (this.getStartDateRangeErrorMessage()) {
        return true;
      }
    }

    if (this.repeatType === RepeatType.monthly) {
      const hasSelectedDay = (
        this.includeSunday ||
        this.includeMonday ||
        this.includeTuesday ||
        this.includeWednesday ||
        this.includeThursday ||
        this.includeFriday ||
        this.includeSaturday
      );
      if (!hasSelectedDay) {
        return true;
      }
    }

    return false;
  }

  private setStartDatesPerInterval() {
    this.duplicateStartDates = [];

    const firstStartDateMoment = moment(this.firstStartDate);
    const lastStartDateMoment = moment(this.lastStartDate);
    let thisStartDateMoment = firstStartDateMoment;

    while (thisStartDateMoment <= lastStartDateMoment) {
      this.duplicateStartDates.push({
        date: getISO8601DateStringFromMoment(thisStartDateMoment),
        errorMessage: ''
      });
      thisStartDateMoment = this.getNextStartDatePerInterval(thisStartDateMoment);
    }
  }

  private getNextStartDatePerInterval(thisStartDate: moment.Moment): moment.Moment {
    if (this.interval === Interval.weekly) {
      return thisStartDate.clone().add(1, 'week');
    } else if (this.interval === Interval.monthly) {
      const wkday = thisStartDate.isoWeekday();

      // Calculate the week number.
      let wkOffset = 0;
      const thisWkdayOfMonth = getFirstWkdayOfMonth(thisStartDate, wkday);
      while (thisWkdayOfMonth < thisStartDate) {
        wkOffset++;
        thisWkdayOfMonth.add(1, 'week');
      }
      if (wkOffset > 3) { wkOffset = 3; } // Don't allow fifth week (may not exist).

      return getFirstWkdayOfMonth(
        thisStartDate.clone().startOf('month').add(1, 'month'),
        wkday
      ).add(wkOffset, 'week')
    } else {
      throw new Error('not implemented');
    }
  }
}

function getFirstWkdayOfMonth(anyDateInMonth: moment.Moment, wkday: number) {
  const firstWkdayThisMonth = anyDateInMonth.clone().startOf('month');
  while (firstWkdayThisMonth.isoWeekday() !== wkday) {
    firstWkdayThisMonth.add(1, 'day');
  }
  return firstWkdayThisMonth;
}
